import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Drawer,
  FormControlLabel,
  IconButton,
  InputAdornment,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
  alpha,
} from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import EmailRoundedIcon from '@mui/icons-material/EmailRounded';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import HowToRegRoundedIcon from '@mui/icons-material/HowToRegRounded';
import PersonOutlineRoundedIcon from '@mui/icons-material/PersonOutlineRounded';
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded';
import MeetingRoomRoundedIcon from '@mui/icons-material/MeetingRoomRounded';
import PaymentsRoundedIcon from '@mui/icons-material/PaymentsRounded';
import SettingsSuggestRoundedIcon from '@mui/icons-material/SettingsSuggestRounded';
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined';
import LoginRoundedIcon from '@mui/icons-material/LoginRounded';
import AutoFixHighRoundedIcon from '@mui/icons-material/AutoFixHighRounded';
import moment from 'moment';
import {
  createWebCheckInInvite,
  getHotelCommunicationSettings,
  getRateForDate,
  isCommEventEnabled,
} from '../../services/api';
import {
  DASHBOARD_ACCENT,
  getDashboardDarkSurfaceTokens,
  resolveDashboardThemeMode,
} from '../../utils/dashboardSurfaceSx';
import { THEME_COLORS, Z_INDEX } from '../../shared/constants/theme';
import countryCodesData from '../../data/country-codes.json';
import countriesData from '../../data/countries-states-cities-data';
import {
  formatPhoneLengthLabel,
  getPhoneMaxLength,
  normalizePhoneInputForCountry,
  resolveDialCodeForCountryInput,
  validatePhoneForCountry,
} from '../../utils/phoneValidation';
import {
  BOOKING_DRAWER_ACCENT,
  BOOKING_DRAWER_WIDTH_SX,
  getBookingDrawerDividerColor,
  getBookingDrawerFooterSx,
  getBookingDrawerHeaderSx,
  getBookingDrawerMenuProps,
  getBookingDrawerPaperSx,
  withDrawerMenuPaperSx,
} from '../../utils/bookingDrawerLayout';

const WA_GREEN = '#25D366';
const ACCENT = DASHBOARD_ACCENT;

const initialForm = {
  name: '',
  email: '',
  phone: '',
  paymentRequirement: 'pay_at_hotel',
  bookingTotalAmount: '',
  paymentAmount: '',
  linkExpiryMinutes: 60,
  autoCheckIn: false,
};

const LINK_EXPIRY_OPTIONS = [
  { value: 15, label: '15 min' },
  { value: 30, label: '30 min' },
  { value: 60, label: '1 hr' },
  { value: 360, label: '6 hr' },
  { value: 720, label: '12 hr' },
  { value: 1440, label: '24 hr' },
  { value: 2880, label: '48 hr' },
];

const paymentOptions = [
  {
    value: 'pay_at_hotel',
    label: 'Pay at hotel',
    hint: 'Set room price · collect later',
  },
  {
    value: 'full',
    label: 'Full payment',
    hint: 'Collect full room price online',
  },
  {
    value: 'partial',
    label: 'Partial payment',
    hint: 'Collect 50% deposit online',
  },
];

const formatAmount = (value) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) && numeric > 0 ? numeric.toFixed(2) : '';
};

const halfAmount = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric) || numeric < 0) return '';
  return (Math.round(numeric * 50) / 100).toFixed(2);
};

const paymentChipLabel = (requirement) => {
  if (requirement === 'full') return 'Full';
  if (requirement === 'partial') return 'Partial';
  if (requirement === 'pay_at_hotel') return 'Pay at hotel';
  return 'No payment';
};

function getConfiguredPhoneDialCode() {
  const hotel = window.DJANGO_CONTEXT?.selectedHotel || null;
  const resolved = resolveDialCodeForCountryInput(
    hotel?.country,
    countryCodesData,
    countriesData.countries
  );
  return resolved || '+91';
}

function ChannelSendButton({
  title,
  tooltip,
  disabled,
  loading,
  onClick,
  icon,
  variant,
  colorSx,
}) {
  const button = (
    <Button
      variant={variant}
      onClick={onClick}
      disabled={disabled || loading}
      fullWidth
      startIcon={loading ? <CircularProgress size={16} color="inherit" /> : icon}
      sx={{
        width: '100%',
        minWidth: 0,
        height: 42,
        borderRadius: 2,
        fontWeight: 800,
        fontSize: '0.875rem',
        letterSpacing: 0.1,
        textTransform: 'none',
        whiteSpace: 'nowrap',
        opacity: disabled && !loading ? 0.55 : 1,
        ...colorSx,
      }}
    >
      {title}
    </Button>
  );

  return (
    <Box sx={{ flex: '1 1 0', minWidth: 0, width: '100%' }}>
      {tooltip ? (
        <Tooltip title={tooltip} arrow placement="top">
          <span style={{ display: 'block', width: '100%' }}>{button}</span>
        </Tooltip>
      ) : (
        button
      )}
    </Box>
  );
}

export default function WebCheckInInviteDialog({
  open,
  context,
  onClose,
  onSuccess,
  themeMode = 'light',
}) {
  const isDark = resolveDashboardThemeMode(themeMode) === 'dark';
  const tokens = getDashboardDarkSurfaceTokens(isDark);
  const [form, setForm] = useState(initialForm);
  const [phoneCountryCode, setPhoneCountryCode] = useState('+91');
  const [phoneError, setPhoneError] = useState('');
  const [phoneCountryCodeMenuOpen, setPhoneCountryCodeMenuOpen] = useState(false);
  const [phoneCountryCodeSearch, setPhoneCountryCodeSearch] = useState('');
  const phoneCountryCodeAnchorRef = useRef(null);
  const [saving, setSaving] = useState('');
  const [error, setError] = useState('');
  const [rateLoading, setRateLoading] = useState(false);
  const [rateError, setRateError] = useState('');
  const [rateTouched, setRateTouched] = useState(false);
  const rateTouchedRef = useRef(false);
  const depositTouchedRef = useRef(false);
  const [suggestedRate, setSuggestedRate] = useState('');
  const [commSettings, setCommSettings] = useState(null);
  const [commLoading, setCommLoading] = useState(false);
  const [commError, setCommError] = useState('');

  useEffect(() => {
    rateTouchedRef.current = rateTouched;
  }, [rateTouched]);

  useEffect(() => {
    if (!open) return undefined;
    const dialCode = getConfiguredPhoneDialCode();
    setPhoneCountryCode(dialCode);
    setPhoneError('');
    setPhoneCountryCodeMenuOpen(false);
    setPhoneCountryCodeSearch('');
    setForm({ ...initialForm });
    setError('');
    setSaving('');
    setRateLoading(false);
    setRateError('');
    setRateTouched(false);
    rateTouchedRef.current = false;
    depositTouchedRef.current = false;
    setSuggestedRate('');
    setCommSettings(null);
    setCommError('');
    setCommLoading(true);

    let cancelled = false;
    getHotelCommunicationSettings()
      .then((data) => {
        if (!cancelled) setCommSettings(data || {});
      })
      .catch(() => {
        if (!cancelled) {
          setCommSettings(null);
          setCommError('Unable to load messaging preferences.');
        }
      })
      .finally(() => {
        if (!cancelled) setCommLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open]);

  const checkInDate = context?.checkInDate || moment().format('YYYY-MM-DD');
  const checkOutDate = useMemo(
    () => moment(checkInDate, 'YYYY-MM-DD', true).add(1, 'day').format('YYYY-MM-DD'),
    [checkInDate]
  );

  const stayLabel = useMemo(() => {
    const cin = moment(checkInDate, 'YYYY-MM-DD', true);
    const cout = moment(checkOutDate, 'YYYY-MM-DD', true);
    if (!cin.isValid() || !cout.isValid()) return 'Stay dates unavailable';
    return `${cin.format('DD MMM')} → ${cout.format('DD MMM YYYY')}`;
  }, [checkInDate, checkOutDate]);

  const emailEnabled = Boolean(
    commSettings && isCommEventEnabled(commSettings, 'web_checkin', 'email', 'guest')
  );
  const whatsappEnabled = Boolean(
    commSettings && isCommEventEnabled(commSettings, 'web_checkin', 'whatsapp', 'guest')
  );

  const emailDisabledReason = useMemo(() => {
    if (commLoading) return 'Loading messaging preferences…';
    if (commError || !commSettings) {
      return 'Unable to verify email settings. Open Configuration → Communication settings.';
    }
    if (!commSettings.email_enabled) {
      return 'Email is turned off for this hotel. Enable Email under Configuration → Communication settings.';
    }
    if (!isCommEventEnabled(commSettings, 'web_checkin', 'email', 'guest')) {
      return 'Guest email for web check-in is disabled. Enable it under Configuration → Communication settings.';
    }
    return '';
  }, [commError, commLoading, commSettings]);

  const whatsappDisabledReason = useMemo(() => {
    if (commLoading) return 'Loading messaging preferences…';
    if (commError || !commSettings) {
      return 'Unable to verify WhatsApp settings. Open Configuration → Communication settings.';
    }
    if (!commSettings.whatsapp_enabled) {
      return 'Automated WhatsApp is off. Send WhatsApp will open WhatsApp Web with the invite message.';
    }
    if (!isCommEventEnabled(commSettings, 'web_checkin', 'whatsapp', 'guest')) {
      return 'Automated WhatsApp is off. Send WhatsApp will open WhatsApp Web with the invite message.';
    }
    return '';
  }, [commError, commLoading, commSettings]);

  const updateField = (field) => (event) => {
    const value = event.target.value;
    setForm((prev) => {
      const next = { ...prev, [field]: value };
      if (field === 'bookingTotalAmount') {
        if (prev.paymentRequirement === 'full') {
          next.paymentAmount = value;
        } else if (prev.paymentRequirement === 'partial' && !depositTouchedRef.current) {
          next.paymentAmount = halfAmount(value);
        }
      }
      return next;
    });
    if (field === 'bookingTotalAmount') setRateTouched(true);
    if (field === 'paymentAmount') depositTouchedRef.current = true;
  };

  const updatePhoneField = (event) => {
    const phone = normalizePhoneInputForCountry(event.target.value, phoneCountryCode);
    setForm((prev) => ({ ...prev, phone }));
    setPhoneError(validatePhoneForCountry(phone, phoneCountryCode, { required: false }));
  };

  const selectPhoneCountryCode = (country) => {
    const normalizedPhone = normalizePhoneInputForCountry(form.phone, country.dialCode);
    setPhoneCountryCode(country.dialCode);
    setForm((prev) => ({ ...prev, phone: normalizedPhone }));
    setPhoneError(validatePhoneForCountry(normalizedPhone, country.dialCode, { required: false }));
    setPhoneCountryCodeMenuOpen(false);
    setPhoneCountryCodeSearch('');
  };

  const selectedPhoneCountryCode = useMemo(
    () => countryCodesData.find((cc) => cc.dialCode === phoneCountryCode)
      || countryCodesData.find((cc) => cc.code === 'IN'),
    [phoneCountryCode]
  );

  const filteredPhoneCountryCodes = useMemo(() => {
    const search = phoneCountryCodeSearch.trim().toLowerCase();
    if (!search) return countryCodesData;
    return countryCodesData.filter((country) => (
      country.name.toLowerCase().includes(search)
      || country.dialCode.includes(search)
      || country.code.toLowerCase().includes(search)
    ));
  }, [phoneCountryCodeSearch]);

  const applyRateToForm = useCallback((rate, requirement) => {
    setForm((prev) => {
      const paymentRequirement = requirement || prev.paymentRequirement;
      let paymentAmount = prev.paymentAmount;
      if (paymentRequirement === 'full') {
        paymentAmount = rate;
      } else if (paymentRequirement === 'partial') {
        paymentAmount = halfAmount(rate);
        depositTouchedRef.current = false;
      } else if (paymentRequirement === 'pay_at_hotel') {
        paymentAmount = '';
      }
      return {
        ...prev,
        bookingTotalAmount: rate,
        paymentAmount,
      };
    });
    setRateTouched(false);
    rateTouchedRef.current = false;
  }, []);

  const fetchRoomRate = useCallback(async ({ apply = true, requirement, force = false } = {}) => {
    if (!context?.roomTypeId || !checkInDate) {
      setRateError('Daily rate is unavailable for this room.');
      setSuggestedRate('');
      return '';
    }

    setRateLoading(true);
    setRateError('');
    try {
      const rateData = await getRateForDate(checkInDate, context.roomTypeId);
      const rate = formatAmount(rateData?.final_rate ?? rateData?.base_rate);
      setSuggestedRate(rate);
      if (!rate) {
        const mode = requirement || form.paymentRequirement;
        const allowZero = mode === 'full' || mode === 'pay_at_hotel';
        setRateError(allowZero ? '' : 'No rate configured for this room and date. Enter a room price.');
        if (apply && force && allowZero) {
          applyRateToForm('0.00', requirement);
        }
        return '';
      }
      if (apply && (force || !rateTouchedRef.current)) {
        applyRateToForm(rate, requirement);
      }
      return rate;
    } catch {
      setRateError('Unable to fetch room price for this room.');
      return '';
    } finally {
      setRateLoading(false);
    }
  }, [applyRateToForm, checkInDate, context?.roomTypeId, form.paymentRequirement]);

  useEffect(() => {
    if (!open || form.paymentRequirement === 'none') return undefined;
    let cancelled = false;
    fetchRoomRate({
      apply: true,
      requirement: form.paymentRequirement,
      force: false,
    }).then(() => {
      if (cancelled) return;
    });
    return () => {
      cancelled = true;
    };
  }, [checkInDate, context?.roomTypeId, form.paymentRequirement, open, fetchRoomRate]);

  const selectPaymentOption = (value) => {
    setForm((prev) => {
      const next = value === prev.paymentRequirement ? 'none' : value;
      if (next === 'partial') {
        depositTouchedRef.current = false;
      }
      return {
        ...prev,
        paymentRequirement: next,
        paymentAmount:
          next === 'full'
            ? prev.bookingTotalAmount
            : next === 'partial'
              ? halfAmount(prev.bookingTotalAmount)
              : '',
        bookingTotalAmount: next === 'none' ? '' : prev.bookingTotalAmount,
      };
    });
  };

  const handleSend = async (channel = 'email') => {
    setError('');
    const sendEmail = channel === 'email' || channel === 'both';
    const sendWhatsApp = channel === 'whatsapp' || channel === 'both';

    if (sendEmail && !emailEnabled) {
      setError(emailDisabledReason || 'Guest email for web check-in is disabled.');
      return;
    }
    if (sendWhatsApp && commLoading) {
      setError('Loading messaging preferences…');
      return;
    }

    const useWhatsAppWeb = sendWhatsApp && !commLoading && !whatsappEnabled;
    const name = form.name.trim();
    const email = form.email.trim();
    const phone = form.phone.trim();
    const paymentRequirement = form.paymentRequirement || 'none';
    const bookingTotalAmount = Number(form.bookingTotalAmount);
    const paymentAmount = Number(
      paymentRequirement === 'full'
        ? form.bookingTotalAmount
        : paymentRequirement === 'partial'
          ? form.paymentAmount
          : 0
    );
    if (!name) {
      setError('Customer name is required.');
      return;
    }
    if (sendEmail && !email) {
      setError('Email address is required to send email.');
      return;
    }
    if (sendWhatsApp && !phone) {
      setError('Phone number is required to send WhatsApp.');
      return;
    }
    if (phone) {
      const nextPhoneError = validatePhoneForCountry(phone, phoneCountryCode, {
        required: sendWhatsApp,
      });
      if (nextPhoneError) {
        setPhoneError(nextPhoneError);
        setError(nextPhoneError);
        return;
      }
    }
    setPhoneError('');
    if (!context?.roomId) {
      setError('Room details are missing. Please reopen the room menu.');
      return;
    }
    if (paymentRequirement !== 'none') {
      if (rateLoading) {
        setError('Please wait while room price is fetched.');
        return;
      }
      if (!Number.isFinite(bookingTotalAmount) || bookingTotalAmount < 0) {
        setError(rateError || 'Room price is required for paid web check-in.');
        return;
      }
      if (paymentRequirement === 'full' || paymentRequirement === 'partial') {
        if (!Number.isFinite(paymentAmount) || paymentAmount < 0) {
          setError('Enter a valid payment amount.');
          return;
        }
        if (paymentRequirement === 'partial' && paymentAmount <= 0) {
          setError('Enter a deposit amount to collect now.');
          return;
        }
        if (paymentAmount > bookingTotalAmount) {
          setError('Payment amount cannot be more than the room price.');
          return;
        }
      }
    }

    setSaving(channel);
    try {
      const response = await createWebCheckInInvite({
        room_id: context.roomId,
        check_in_date: checkInDate,
        check_out_date: checkOutDate,
        name,
        email,
        phone,
        country_code: phoneCountryCode || '+91',
        send_email: sendEmail,
        send_whatsapp: sendWhatsApp && !useWhatsAppWeb,
        payment_requirement: paymentRequirement,
        booking_total_amount: paymentRequirement === 'none' ? '' : bookingTotalAmount,
        payment_amount:
          paymentRequirement === 'none' || paymentRequirement === 'pay_at_hotel'
            ? ''
            : paymentAmount,
        payment_currency: 'INR',
        link_expiry_minutes: Number(form.linkExpiryMinutes) || 60,
        auto_check_in: Boolean(form.autoCheckIn),
      });
      if (sendWhatsApp && useWhatsAppWeb) {
        const message = (response?.whatsapp_message || '').trim();
        const toPhone = String(response?.whatsapp_to || '').replace(/\D/g, '')
          || [
            String(phoneCountryCode || '').replace(/\D/g, ''),
            String(phone || '').replace(/\D/g, ''),
          ].join('');
        if (!message || !toPhone) {
          throw new Error('Check-in link was created but could not open WhatsApp Web.');
        }
        window.open(
          `https://wa.me/${toPhone}?text=${encodeURIComponent(message)}`,
          '_blank',
          'noopener,noreferrer'
        );
      } else if (sendWhatsApp) {
        if (!response?.whatsapp_sent) {
          throw new Error(response?.whatsapp_user_message || 'WhatsApp message was not sent.');
        }
      }
      onSuccess?.(response, channel);
      onClose?.();
    } catch (err) {
      setError(err?.userMessage || err?.message || 'Unable to send web check-in link.');
    } finally {
      setSaving('');
    }
  };

  const softBorder = tokens.surfaceBorder;
  const muted = tokens.subtleText;
  const sectionBg = isDark ? alpha('#ffffff', 0.035) : alpha(ACCENT, 0.04);
  const fieldBg = isDark ? alpha('#ffffff', 0.045) : alpha('#0f172a', 0.03);

  const textFieldSx = {
    width: '100%',
    '& .MuiOutlinedInput-root': {
      borderRadius: 2,
      minHeight: 48,
      color: tokens.text,
      backgroundColor: fieldBg,
      transition: 'box-shadow 160ms ease, border-color 160ms ease',
      '& fieldset': {
        borderColor: softBorder,
      },
      '&:hover fieldset': {
        borderColor: alpha(ACCENT, 0.55),
      },
      '&.Mui-focused': {
        boxShadow: `0 0 0 3px ${alpha(ACCENT, 0.16)}`,
        '& fieldset': { borderColor: ACCENT },
      },
    },
    '& .MuiInputBase-input': {
      py: 1.25,
      fontSize: '0.95rem',
    },
    '& .MuiInputLabel-root': {
      color: muted,
      fontSize: '0.92rem',
      '&.Mui-focused': { color: ACCENT },
    },
    '& .MuiFormHelperText-root': {
      mx: 0.25,
      mt: 0.55,
      mb: 0,
      minHeight: 18,
      lineHeight: 1.3,
      fontSize: '0.74rem',
      color: muted,
    },
    '& .MuiInputAdornment-root': {
      color: muted,
      '& .MuiTypography-root': {
        color: muted,
        fontWeight: 700,
        fontSize: '0.82rem',
      },
    },
  };

  const sectionSx = {
    p: 1.75,
    borderRadius: 2.5,
    border: `1px solid ${softBorder}`,
    bgcolor: sectionBg,
  };

  const bothChannelsDisabled = !commLoading && !emailEnabled && !whatsappEnabled;
  const showPaymentAmounts = form.paymentRequirement !== 'none';

  return (
    <Drawer
      anchor="right"
      open={Boolean(open && context)}
      onClose={saving ? undefined : onClose}
      ModalProps={{
        sx: { zIndex: Z_INDEX.DRAWER },
      }}
      PaperProps={{
        sx: {
          width: BOOKING_DRAWER_WIDTH_SX,
          height: '100%',
          overflow: 'hidden',
          ...getBookingDrawerPaperSx(isDark, {
            backgroundColor: isDark ? '#1e1e24' : '#f5f5f5',
          }),
        },
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', position: 'relative' }}>
        <Box
          sx={{
            ...getBookingDrawerHeaderSx(isDark),
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            p: 1.5,
            borderBottom: `2px solid ${getBookingDrawerDividerColor(isDark)}`,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, flex: 1, pr: 1 }}>
            <HowToRegRoundedIcon sx={{ color: BOOKING_DRAWER_ACCENT, fontSize: 24, flexShrink: 0 }} />
            <Typography
              variant="h6"
              sx={{ color: isDark ? tokens.text : '#333', fontWeight: 700 }}
              noWrap
            >
              Web Check-in
            </Typography>
            <Chip
              size="small"
              icon={<MeetingRoomRoundedIcon sx={{ fontSize: '15px !important' }} />}
              label={`Room ${context?.roomNumber || context?.roomId || '—'}`}
              sx={{
                height: 24,
                fontWeight: 700,
                fontSize: '0.72rem',
                bgcolor: alpha(BOOKING_DRAWER_ACCENT, isDark ? 0.14 : 0.16),
                color: isDark ? BOOKING_DRAWER_ACCENT : '#0f766e',
                border: `1px solid ${alpha(BOOKING_DRAWER_ACCENT, 0.35)}`,
                '& .MuiChip-icon': { color: 'inherit', ml: 0.5 },
                '& .MuiChip-label': { px: 0.85 },
              }}
            />
            <Chip
              size="small"
              icon={<CalendarMonthRoundedIcon sx={{ fontSize: '15px !important' }} />}
              label={stayLabel}
              sx={{
                height: 24,
                fontWeight: 700,
                fontSize: '0.72rem',
                bgcolor: isDark ? alpha('#ffffff', 0.06) : alpha('#0f172a', 0.05),
                color: isDark ? tokens.subtleText : '#475569',
                border: `1px solid ${getBookingDrawerDividerColor(isDark)}`,
                display: { xs: 'none', sm: 'inline-flex' },
                '& .MuiChip-icon': { color: 'inherit', ml: 0.5 },
                '& .MuiChip-label': { px: 0.85 },
              }}
            />
          </Box>
          <IconButton
            onClick={onClose}
            size="small"
            disabled={Boolean(saving)}
            aria-label="Close web check-in drawer"
          >
            <CloseRoundedIcon />
          </IconButton>
        </Box>

        <Box
          sx={{
            flex: 1,
            overflowY: 'auto',
            p: 2,
            WebkitOverflowScrolling: 'touch',
            bgcolor: isDark ? '#1e1e24' : '#f5f5f5',
          }}
        >
        <Stack spacing={1.7}>
          {error ? <Alert severity="error" sx={{ borderRadius: 2, py: 0.4 }}>{error}</Alert> : null}
          {bothChannelsDisabled ? (
            <Alert
              severity="warning"
              icon={<SettingsSuggestRoundedIcon sx={{ fontSize: 20 }} />}
              sx={{
                borderRadius: 2,
                py: 0.35,
                alignItems: 'center',
                '& .MuiAlert-message': { fontSize: '0.82rem', lineHeight: 1.4, py: 0.4 },
                '& .MuiAlert-icon': { py: 0.7, mr: 1 },
              }}
            >
              Guest email is disabled and automated WhatsApp is off. Send WhatsApp still opens
              WhatsApp Web with the invite. Enable email under{' '}
              <strong>Configuration → Communication settings</strong>.
            </Alert>
          ) : null}

          <Box sx={sectionSx}>
            <Stack direction="row" spacing={0.85} alignItems="center" sx={{ mb: 1.35 }}>
              <PersonOutlineRoundedIcon sx={{ fontSize: 18, color: ACCENT }} />
              <Typography sx={{ fontWeight: 800, fontSize: '0.88rem', letterSpacing: 0.15 }}>
                Guest details
              </Typography>
            </Stack>
            <Stack spacing={1.4}>
              <TextField
                label="Customer Name"
                value={form.name}
                onChange={updateField('name')}
                fullWidth
                required
                size="small"
                sx={textFieldSx}
              />
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                  gap: 1.4,
                  alignItems: 'start',
                }}
              >
                <TextField
                  label="Email Address"
                  value={form.email}
                  onChange={updateField('email')}
                  fullWidth
                  size="small"
                  type="email"
                  helperText={emailEnabled ? 'Required for Send Email / Send Both' : ' '}
                  sx={textFieldSx}
                />
                <TextField
                  label="Phone Number"
                  value={form.phone}
                  onChange={updatePhoneField}
                  onBlur={() => {
                    setPhoneError(
                      validatePhoneForCountry(form.phone, phoneCountryCode, {
                        required: Boolean(form.phone),
                      })
                    );
                  }}
                  fullWidth
                  size="small"
                  type="tel"
                  error={Boolean(phoneError)}
                  helperText={
                    phoneError
                      || `Enter ${formatPhoneLengthLabel(phoneCountryCode)} for WhatsApp`
                  }
                  inputProps={{
                    inputMode: 'numeric',
                    pattern: '[0-9]*',
                    maxLength: getPhoneMaxLength(phoneCountryCode),
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Box
                          ref={phoneCountryCodeAnchorRef}
                          onMouseDown={(event) => {
                            // Keep focus from bouncing between field and Menu.
                            event.preventDefault();
                            event.stopPropagation();
                          }}
                          onClick={(event) => {
                            event.preventDefault();
                            event.stopPropagation();
                            setPhoneCountryCodeMenuOpen(true);
                          }}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            cursor: 'pointer',
                            gap: 0.4,
                            px: 0.65,
                            py: 0.35,
                            borderRadius: 1,
                            mr: -0.5,
                            '&:hover': {
                              backgroundColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
                            },
                          }}
                        >
                          <Typography component="span" sx={{ fontSize: '1.05rem', lineHeight: 1 }}>
                            {selectedPhoneCountryCode?.flag || '🇮🇳'}
                          </Typography>
                          <Typography component="span" sx={{ fontWeight: 700, fontSize: '0.8rem' }}>
                            {selectedPhoneCountryCode?.dialCode || '+91'}
                          </Typography>
                          <Typography component="span" sx={{ fontSize: '0.65rem', color: muted }}>
                            ▼
                          </Typography>
                        </Box>
                      </InputAdornment>
                    ),
                  }}
                  sx={textFieldSx}
                />
              </Box>
              <Menu
                anchorEl={phoneCountryCodeAnchorRef.current}
                open={phoneCountryCodeMenuOpen}
                onClose={() => {
                  setPhoneCountryCodeMenuOpen(false);
                  setPhoneCountryCodeSearch('');
                }}
                disableAutoFocusItem
                disableScrollLock
                // Portal above the drawer (not disablePortal) so overflow:hidden does not clip
                // the list; keep an opaque paper so All Rooms never shows through.
                {...withDrawerMenuPaperSx(
                  {
                    ...getBookingDrawerMenuProps(isDark, isDark ? '#1e1e24' : '#ffffff'),
                    disablePortal: false,
                  },
                  {
                    maxHeight: 360,
                    width: 280,
                    bgcolor: isDark ? '#1e1e24' : '#ffffff',
                    backgroundColor: isDark ? '#1e1e24' : '#ffffff',
                    backgroundImage: 'none',
                    color: tokens.text,
                    border: `1px solid ${softBorder}`,
                  },
                )}
                slotProps={{
                  root: {
                    sx: { zIndex: Z_INDEX.DRAWER_MENU },
                  },
                }}
              >
                <Box sx={{ p: 1 }}>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Search country..."
                    value={phoneCountryCodeSearch}
                    onChange={(e) => setPhoneCountryCodeSearch(e.target.value)}
                    onKeyDown={(e) => e.stopPropagation()}
                    sx={{ mb: 0.5, ...textFieldSx }}
                  />
                </Box>
                {filteredPhoneCountryCodes.map((country) => (
                  <MenuItem
                    key={`${country.code}-${country.dialCode}`}
                    onClick={() => selectPhoneCountryCode(country)}
                    selected={selectedPhoneCountryCode?.dialCode === country.dialCode}
                  >
                    <ListItemIcon sx={{ minWidth: 34 }}>
                      <Typography sx={{ fontSize: '1.15rem' }}>{country.flag}</Typography>
                    </ListItemIcon>
                    <ListItemText
                      primary={country.dialCode}
                      secondary={country.name}
                      primaryTypographyProps={{ fontWeight: 700, color: tokens.text }}
                      secondaryTypographyProps={{ color: muted }}
                    />
                  </MenuItem>
                ))}
              </Menu>
            </Stack>
          </Box>

          <Box sx={sectionSx}>
            <Stack direction="row" spacing={0.85} alignItems="center" sx={{ mb: 1.2 }} useFlexGap flexWrap="wrap">
              <PaymentsRoundedIcon sx={{ fontSize: 18, color: ACCENT }} />
              <Typography sx={{ fontWeight: 800, fontSize: '0.88rem', letterSpacing: 0.15 }}>
                Payment option
              </Typography>
              <Chip
                size="small"
                label={paymentChipLabel(form.paymentRequirement)}
                sx={{
                  height: 24,
                  fontWeight: 800,
                  fontSize: '0.72rem',
                  bgcolor: form.paymentRequirement === 'none'
                    ? tokens.chipBg
                    : alpha(ACCENT, 0.16),
                  color: form.paymentRequirement === 'none' ? muted : ACCENT,
                  border: `1px solid ${form.paymentRequirement === 'none' ? softBorder : alpha(ACCENT, 0.3)}`,
                }}
              />
            </Stack>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' },
                gap: 1.15,
              }}
            >
              {paymentOptions.map((option) => {
                const selected = form.paymentRequirement === option.value;
                return (
                  <Box
                    key={option.value}
                    role="button"
                    tabIndex={0}
                    onClick={() => selectPaymentOption(option.value)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        selectPaymentOption(option.value);
                      }
                    }}
                    sx={{
                      cursor: 'pointer',
                      px: 1.4,
                      py: 1.3,
                      minHeight: 76,
                      borderRadius: 2.25,
                      border: `1.5px solid ${selected ? ACCENT : softBorder}`,
                      bgcolor: selected
                        ? alpha(ACCENT, isDark ? 0.14 : 0.1)
                        : (isDark ? alpha('#ffffff', 0.03) : '#ffffff'),
                      boxShadow: selected ? `0 6px 16px ${alpha(ACCENT, 0.12)}` : 'none',
                      transition: 'all 140ms ease',
                      '&:hover': {
                        borderColor: alpha(ACCENT, 0.65),
                        bgcolor: alpha(ACCENT, isDark ? 0.1 : 0.07),
                      },
                    }}
                  >
                    <Typography sx={{ fontWeight: 800, fontSize: '0.9rem', mb: 0.3, lineHeight: 1.25 }}>
                      {option.label}
                    </Typography>
                    <Typography sx={{ color: muted, fontSize: '0.76rem', lineHeight: 1.35 }}>
                      {option.hint}
                    </Typography>
                  </Box>
                );
              })}
            </Box>

            {showPaymentAmounts ? (
              <Box sx={{ mt: 1.45 }}>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  spacing={1}
                  sx={{ mb: 1.1 }}
                  useFlexGap
                  flexWrap="wrap"
                >
                  <Typography sx={{ color: muted, fontSize: '0.78rem', fontWeight: 600 }}>
                    Room price{' '}
                    {form.paymentRequirement === 'full'
                      ? '(collected online)'
                      : form.paymentRequirement === 'partial'
                        ? '(stay total)'
                        : '(due at hotel)'}
                  </Typography>
                  <Button
                    size="small"
                    startIcon={
                      rateLoading
                        ? <CircularProgress size={14} color="inherit" />
                        : <AutoFixHighRoundedIcon sx={{ fontSize: 16 }} />
                    }
                    disabled={rateLoading || Boolean(saving)}
                    onClick={() => fetchRoomRate({ apply: true, requirement: form.paymentRequirement, force: true })}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 800,
                      fontSize: '0.78rem',
                      color: ACCENT,
                      borderRadius: 1.75,
                      px: 1.1,
                      minHeight: 30,
                      border: `1px solid ${alpha(ACCENT, 0.35)}`,
                      bgcolor: alpha(ACCENT, isDark ? 0.1 : 0.08),
                      '&:hover': {
                        bgcolor: alpha(ACCENT, isDark ? 0.16 : 0.12),
                        borderColor: ACCENT,
                      },
                    }}
                  >
                    Auto-fill rate
                  </Button>
                </Stack>

                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: {
                      xs: '1fr',
                      sm: form.paymentRequirement === 'partial' ? '1fr 1fr' : '1fr',
                    },
                    gap: 1.4,
                    alignItems: 'start',
                  }}
                >
                  <TextField
                    label={rateLoading ? 'Fetching room price…' : 'Room price'}
                    value={form.bookingTotalAmount}
                    onChange={updateField('bookingTotalAmount')}
                    fullWidth
                    required
                    size="small"
                    type="number"
                    inputProps={{ min: 0, step: '0.01' }}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">INR</InputAdornment>,
                    }}
                    sx={textFieldSx}
                    helperText={
                      rateError
                      || (suggestedRate
                        ? `Suggested from Manage Rates: INR ${suggestedRate}`
                        : 'Editable — auto-fill uses Manage Rates for this room/date.')
                    }
                    error={Boolean(rateError)}
                  />
                  {form.paymentRequirement === 'partial' ? (
                    <TextField
                      label="Collect now"
                      value={form.paymentAmount}
                      onChange={updateField('paymentAmount')}
                      fullWidth
                      required
                      size="small"
                      type="number"
                      inputProps={{ min: 0, step: '0.01' }}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">INR</InputAdornment>,
                      }}
                      sx={textFieldSx}
                      helperText="Defaults to 50% of room price · editable"
                    />
                  ) : null}
                </Box>

                {form.paymentRequirement === 'full' ? (
                  <Typography sx={{ color: muted, fontSize: '0.75rem', mt: 1, lineHeight: 1.4 }}>
                    Guest pays the full room price via Razorpay before check-in completes.
                  </Typography>
                ) : form.paymentRequirement === 'partial' ? (
                  <Typography sx={{ color: muted, fontSize: '0.75rem', mt: 1, lineHeight: 1.4 }}>
                    Guest pays the deposit online now. Remaining balance can be collected at the hotel.
                  </Typography>
                ) : (
                  <Typography sx={{ color: muted, fontSize: '0.75rem', mt: 1, lineHeight: 1.4 }}>
                    No online payment. Room price is saved on the booking and collected at the hotel.
                  </Typography>
                )}
              </Box>
            ) : (
              <Typography sx={{ color: muted, fontSize: '0.78rem', mt: 1.2, lineHeight: 1.4 }}>
                Leave payment unselected for free check-in. Booking is created after the guest submits.
              </Typography>
            )}
          </Box>

          <Box sx={sectionSx}>
            <Stack direction="row" alignItems="center" spacing={0.85} sx={{ mb: 1.05 }}>
              <TimerOutlinedIcon sx={{ fontSize: 18, color: ACCENT }} />
              <Typography sx={{ fontWeight: 800, fontSize: '0.88rem' }}>Link expiry</Typography>
            </Stack>
            <Stack direction="row" flexWrap="wrap" useFlexGap spacing={0.9}>
              {LINK_EXPIRY_OPTIONS.map((option) => {
                const selected = Number(form.linkExpiryMinutes) === option.value;
                return (
                  <Chip
                    key={option.value}
                    label={option.label}
                    clickable
                    onClick={() => setForm((prev) => ({ ...prev, linkExpiryMinutes: option.value }))}
                    sx={{
                      height: 32,
                      fontWeight: 700,
                      fontSize: '0.8rem',
                      borderRadius: 1.75,
                      border: `1.5px solid ${selected ? ACCENT : softBorder}`,
                      bgcolor: selected
                        ? alpha(ACCENT, isDark ? 0.16 : 0.12)
                        : tokens.chipBg,
                      color: selected ? (isDark ? '#ecfdf5' : '#065f46') : tokens.text,
                      '&:hover': {
                        bgcolor: alpha(ACCENT, isDark ? 0.12 : 0.08),
                      },
                    }}
                  />
                );
              })}
            </Stack>
            <Typography sx={{ color: muted, fontSize: '0.75rem', mt: 0.9, lineHeight: 1.35 }}>
              Guest link expires after the selected time.
            </Typography>
          </Box>

          <Box
            sx={{
              ...sectionSx,
              border: `1.5px solid ${form.autoCheckIn ? ACCENT : softBorder}`,
              bgcolor: form.autoCheckIn
                ? alpha(ACCENT, isDark ? 0.12 : 0.08)
                : sectionBg,
            }}
          >
            <FormControlLabel
              sx={{ m: 0, width: '100%', alignItems: 'flex-start' }}
              control={
                <Switch
                  checked={Boolean(form.autoCheckIn)}
                  onChange={(e) => setForm((prev) => ({ ...prev, autoCheckIn: e.target.checked }))}
                  color="success"
                  sx={{ mt: -0.15 }}
                />
              }
              label={
                <Box sx={{ pl: 0.35 }}>
                  <Stack direction="row" alignItems="center" spacing={0.7}>
                    <LoginRoundedIcon sx={{ fontSize: 18, color: ACCENT }} />
                    <Typography sx={{ fontWeight: 800, fontSize: '0.9rem' }}>
                      Auto check-in on submit
                    </Typography>
                  </Stack>
                  <Typography sx={{ color: muted, fontSize: '0.75rem', lineHeight: 1.4, mt: 0.3 }}>
                    When on, successful guest submission sets the booking to checked in.
                  </Typography>
                </Box>
              }
            />
          </Box>
        </Stack>
        </Box>

        <Box
          sx={{
            flexShrink: 0,
            p: 1.5,
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            ...getBookingDrawerFooterSx(isDark),
          }}
        >
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', width: '100%' }}>
            <ChannelSendButton
              title="Send WhatsApp"
              tooltip={
                whatsappDisabledReason
                || (saving ? '' : 'Send the check-in link on WhatsApp')
              }
              disabled={Boolean(saving) || commLoading}
              loading={saving === 'whatsapp'}
              onClick={() => handleSend('whatsapp')}
              icon={<WhatsAppIcon />}
              variant="outlined"
              colorSx={{
                borderColor: alpha(WA_GREEN, 0.7),
                color: isDark ? WA_GREEN : '#128C7E',
                bgcolor: alpha(WA_GREEN, isDark ? 0.08 : 0.06),
                '&:hover': {
                  borderColor: WA_GREEN,
                  bgcolor: alpha(WA_GREEN, 0.14),
                },
              }}
            />
            <ChannelSendButton
              title="Send Email"
              tooltip={emailDisabledReason || (saving ? '' : 'Email the check-in link to the guest')}
              disabled={Boolean(saving) || !emailEnabled || commLoading}
              loading={saving === 'email'}
              onClick={() => handleSend('email')}
              icon={<EmailRoundedIcon />}
              variant="contained"
              colorSx={{
                bgcolor: emailEnabled ? ACCENT : alpha(isDark ? '#ffffff' : '#0f172a', 0.18),
                color: emailEnabled ? '#031512' : muted,
                boxShadow: emailEnabled ? `0 10px 22px ${alpha(ACCENT, 0.35)}` : 'none',
                '&:hover': {
                  bgcolor: emailEnabled ? THEME_COLORS.PRIMARY_HOVER : alpha(isDark ? '#ffffff' : '#0f172a', 0.18),
                },
              }}
            />
          </Box>
          <ChannelSendButton
            title="Send Both"
            tooltip={
              !emailEnabled
                ? emailDisabledReason
                : (
                  whatsappDisabledReason
                  || (saving ? '' : 'Email the link and send WhatsApp together')
                )
            }
            disabled={Boolean(saving) || !emailEnabled || commLoading}
            loading={saving === 'both'}
            onClick={() => handleSend('both')}
            icon={
              <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.35 }}>
                <WhatsAppIcon sx={{ fontSize: 18 }} />
                <EmailRoundedIcon sx={{ fontSize: 18 }} />
              </Box>
            }
            variant="contained"
            colorSx={{
              bgcolor: emailEnabled
                ? (isDark ? alpha(ACCENT, 0.92) : '#0f766e')
                : alpha(isDark ? '#ffffff' : '#0f172a', 0.18),
              color: emailEnabled ? '#ffffff' : muted,
              boxShadow: emailEnabled ? `0 10px 22px ${alpha('#0f766e', 0.28)}` : 'none',
              '&:hover': {
                bgcolor: emailEnabled
                  ? (isDark ? ACCENT : '#0d9488')
                  : alpha(isDark ? '#ffffff' : '#0f172a', 0.18),
              },
            }}
          />
        </Box>
      </Box>
    </Drawer>
  );
}
